import React, { useState } from "react";
import Select from "react-select";
import trashIcon from '../../../images/trash-icon.png';
import infoIcon from '../../../images/info-icon-img.png';
import { formatUSD } from '../../../utils/common';
import ProductInventoryModal from '../../../components/Modals/ProductInventoryModal';

const EditPurchaseOrderTable = ({
    salesData = [],
    setFormData,
    products = [],
    productMap = {},
    multiplierMap = {},
    multiplierOptions = [],
    isCompany = false,
    rowErrors = {}
}) => {
    const TAX_RATE = 0.00; // 0%
    const [isUpdateSales, setIsUpdateSales] = useState(false);
    const [showInventoryModal, setShowInventoryModal] = useState(false);
    const [selectedProductNumber, setSelectedProductNumber] = useState('');
    const defaultMultiplier = null;

    const handleOpenInventory = (productNumber) => {
        setSelectedProductNumber(productNumber);
        setShowInventoryModal(true);
    };

    const addNewRow = () => {
        setFormData(prev => {
            const rows = [...(prev.sales_data || [])];
            const lastRow = rows.length > 0 ? rows[rows.length - 1] : {};
            const mNumeric = parseNumber(lastRow.multiplier || 1);
            rows.push({
                type: "Sale",
                product_number: "",
                description: "",
                unit_price: "0.00",
                quantity: "",
                total_price: "0.00",
                multiplier: isCompany ? "" : (lastRow.multiplier || defaultMultiplier || mNumeric),
                net_price: "0.00"
            });
            return {
                ...prev,
                sales_data: rows,
                ...recalcTotals(rows)
            };
        });
    };

    const safeSalesData = Array.isArray(salesData) ? salesData : [];

    const parseNumber = (value) => {
        if (value === null || value === undefined || value === "") return 0;
        const num = Number(String(value).replace(/[^0-9.-]+/g, ""));
        return isNaN(num) ? 0 : num;
    };

    const getRowNetPrice = (row) => {
        const netPrice = parseNumber(row.net_price);
        if (netPrice > 0) return netPrice;

        const unitPrice = parseNumber(row.unit_price);
        if (isCompany) {
            const product = productMap ? productMap[row.product_number] : null;
            const mCode = product ? product.multiplier_code : null;
            const mNumeric = parseNumber((multiplierMap && mCode) ? multiplierMap[mCode] : 1);
            return unitPrice * mNumeric;
        } else {
            const mNumeric = parseNumber(row.multiplier || 1);
            return unitPrice * mNumeric;
        }
    };

    const recalcTotals = (rows) => {
        const sub = rows.reduce(
            (sum, r) => sum + parseNumber(r.total_price),
            0
        );
        const tax = sub * TAX_RATE;
        return {
            sub_total: sub.toFixed(2),
            sales_tax: tax.toFixed(2),
            total: (sub + tax).toFixed(2)
        };
    };

    const handleProductSelect = (index, selected) => {
        if (!selected) return;

        const product = productMap[selected.value];
        if (!product) return;

        const unitPrice = parseNumber(product.price);

        // Use product-specific multiplier for customers, 
        // or the existing row's multiplier (falling back to default) for staff.
        const mCode = isCompany
            ? product.multiplier_code
            : (salesData[index].multiplier || defaultMultiplier);

        // console.log(multiplierMap, mCode);
        // console.log(multiplierMap[mCode]);

        const mNumeric = parseNumber(multiplierMap[mCode] || 1);

        setFormData(prev => {
            const rows = [...prev.sales_data];
            const qty = parseNumber(rows[index].quantity || 0);
            const totalPrice = (qty * unitPrice * mNumeric).toFixed(2);
            const netPrice = (unitPrice * mNumeric).toFixed(2);

            rows[index] = {
                ...rows[index],
                product_number: product.num,
                description: product.description,
                unit_price: unitPrice.toFixed(2),
                multiplier: parseNumber(multiplierMap[mCode] || null),
                net_price: netPrice,
                total_price: totalPrice
            };

            console.log("Updated row after product select:", rows[index]);

            return {
                ...prev,
                sales_data: rows,
                ...recalcTotals(rows)
            };
        });
    };

    const handleQtyChange = (index, value) => {
        const qtyNum = parseNumber(value);

        setFormData(prev => {
            const rows = [...prev.sales_data];
            const currentRow = rows[index];
            const price = parseNumber(currentRow.unit_price);
            //const mCode = currentRow.multiplier || defaultMultiplier;
            const mNumeric = parseNumber(currentRow.multiplier || 1);
            const netPrice = (price * mNumeric).toFixed(2);

            rows[index] = {
                ...currentRow,
                quantity: value,
                net_price: netPrice,
                total_price: (qtyNum * price * mNumeric).toFixed(2)
            };

            // Infinite row logic: add a new row if we're typing in the last row
            if (qtyNum > 0 && index === rows.length - 1) {
                rows.push({
                    type: "Sale",
                    product_number: "",
                    description: "",
                    unit_price: "0.00",
                    quantity: "",
                    total_price: "0.00",
                    multiplier: mNumeric,
                    net_price: "0.00"
                });
            }

            return {
                ...prev,
                sales_data: rows,
                ...recalcTotals(rows)
            };
        });
    };

    const handleMultiplierSelect = (index, selected) => {
        if (!selected) return;

        const mCode = selected.value;
        const mNumeric = parseNumber(multiplierMap[mCode] || 1);

        setFormData(prev => {
            const rows = [...prev.sales_data];
            const unitPrice = parseNumber(rows[index].unit_price || 0);
            const qty = parseNumber(rows[index].quantity || 0);
            const netPrice = (unitPrice * mNumeric).toFixed(2);

            rows[index] = {
                ...rows[index],
                multiplier: mNumeric,
                net_price: netPrice,
                total_price: (unitPrice * mNumeric * qty).toFixed(2)
            };

            return {
                ...prev,
                sales_data: rows,
                ...recalcTotals(rows)
            };
        });
    };

    const removeRow = (index) => {
        setFormData(prev => {
            const rows = prev.sales_data.filter((_, i) => i !== index);
            return {
                ...prev,
                sales_data: rows,
                ...recalcTotals(rows)
            };
        });
    };

    return (
        <React.Fragment>
            <table className="table table-bordered techtop-purchase-order-table2">
                <thead>
                    <tr>
                        <th className="text-center" style={{ width: "6%" }}>Item #</th>
                        <th className="text-center">Type</th>
                        <th className="text-center" style={{ width: "25%" }}>Number</th>
                        <th className="text-center" style={{ width: (isCompany) ? "30%" : "25%" }}>Description</th>
                        <th className="text-center">List Price</th>
                        <th className="text-center" style={{ width: "8%" }}>Qty</th>
                        <th className="text-center">Net Price</th>
                        {!isCompany && <th className="text-center">Multiplier</th>}
                        <th className="text-center">Total</th>
                        <th className="text-center">Inventory</th>
                        <th className="text-center">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {safeSalesData.map((row, index) => (
                        <tr key={index}>
                            <td className="text-center">{index + 1}</td>
                            <td className="text-center">{row.type}</td>
                            <td>
                                <Select
                                    options={products}
                                    value={row.product_number ? { value: row.product_number, label: row.product_number } : null}
                                    onChange={(opt) => handleProductSelect(index, opt)}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                />
                                {rowErrors?.[index]?.product_number && (
                                    <div className="text-danger small">{rowErrors[index].product_number}</div>
                                )}
                            </td>
                            <td>{row.description}</td>
                            <td className="text-center">
                                ${formatUSD(parseNumber(row.unit_price))}
                            </td>
                            <td>
                                <input
                                    type="number"
                                    className="form-control quantity-input"
                                    value={row.quantity}
                                    min="1"
                                    onChange={(e) => handleQtyChange(index, e.target.value)}
                                />
                                {rowErrors?.[index]?.quantity && (
                                    <div className="invalid-feedback d-block">{rowErrors[index].quantity}</div>
                                )}
                            </td>
                            <td className="text-center">
                                ${formatUSD(
                                    (isCompany && row.product_number) || (!isCompany && row.multiplier)
                                        ? getRowNetPrice(row)
                                        : 0
                                )}
                            </td>
                            {!isCompany && (
                                <td>
                                    <Select
                                        options={multiplierOptions}
                                        // Ensure the dropdown reflects the value stored in the row
                                        value={multiplierOptions.find(opt => opt.value === row.multiplier) || null}
                                        onChange={(opt) => handleMultiplierSelect(index, opt)}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                    {rowErrors?.[index]?.multiplier && (
                                        <div className="invalid-feedback d-block">{rowErrors[index].multiplier}</div>
                                    )}
                                </td>
                            )}
                            <td className="text-center">
                                ${formatUSD(parseNumber(row.total_price))}
                            </td>
                            <td className="text-center">
                                {row.product_number && (
                                    <img
                                        src={infoIcon}
                                        alt="Inventory Info"
                                        title="View Inventory"
                                        style={{ cursor: 'pointer', width: '18px', marginRight: safeSalesData.length > 1 ? '8px' : '0px' }}
                                        onClick={() => handleOpenInventory(row.product_number)}
                                    />
                                )}
                            </td>
                            <td className="text-center">
                                {safeSalesData.length > 1 && (
                                    <button
                                        type="button"
                                        className="btn-link-style"
                                        onClick={() => removeRow(index)}
                                        title="Remove row"
                                    >
                                        <img
                                            className="remove-icon"
                                            src={trashIcon}
                                            alt="Remove"
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="d-flex justify-content-end mt-2 mb-3">
                <button
                    type="button"
                    onClick={addNewRow}
                    title="Add Line Item"
                    style={{
                        backgroundColor: '#fa4616',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        lineHeight: '1',
                        padding: '0',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                    }}
                >
                    +
                </button>
            </div>
            <ProductInventoryModal
                isShow={showInventoryModal}
                productNumber={selectedProductNumber}
                onClose={() => setShowInventoryModal(false)}
            />
        </React.Fragment>
    );
};

export default React.memo(EditPurchaseOrderTable);